import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Sparkles, ExternalLink, SlidersHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useNavigationDirection } from '../contexts/NavigationContext';
import { LinkifyText } from './LinkifyText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getPageVariants = (direction) => ({
  initial: { opacity: 0, x: direction * 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: direction * -30 }
});

const pageTransition = { duration: 0.25, ease: 'easeOut' };

function ActivityPage({ user }) {
  const { direction } = useNavigationDirection();
  const navigate = useNavigate();
  const [activity, setActivity] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => { fetchActivity(); }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilterMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${API}/activity`);
      setActivity(response.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  };

  const handleViewItem = (item) => {
    if (item.type === 'journal') {
      navigate(`/entry/${item.id}/edit`);
    } else if (item.topic_folder_id) {
      navigate(`/stories/${item.topic_folder_id}`);
    } else {
      navigate('/stories/unsorted');
    }
  };

  const filteredActivity = activity
    .filter(item => filterType === 'all' || (filterType === 'experience' ? item.type === 'experience' : item.type === filterType))
    .sort((a, b) => {
      const da = new Date(a.timestamp), db = new Date(b.timestamp);
      return sortOrder === 'newest' ? db - da : da - db;
    });

  const isFiltered = filterType !== 'all' || sortOrder !== 'newest';

  return (
    <motion.div variants={getPageVariants(direction)} initial="initial" animate="animate" exit="exit" transition={pageTransition} className="min-h-screen pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-start justify-between mb-12">
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-tight text-foreground mb-1" data-testid="activity-page-title">All Activity</h1>
            <p className="text-base text-muted-foreground leading-relaxed">Your complete journaling timeline</p>
          </div>
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className={`p-1.5 rounded-full transition-all ${isFiltered ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="activity-filter-button"
              title="Filter & Sort"
            >
              <SlidersHorizontal className="w-5 h-5" />
            </button>
            {showFilterMenu && (
              <div className="absolute top-full mt-1 right-0 bg-card-solid border border-border rounded-xl shadow-lg py-1 z-20 min-w-[160px]" data-testid="activity-filter-menu">
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Type</div>
                {[
                  { key: 'all', label: 'All' },
                  { key: 'journal', label: 'Entries Only' },
                  { key: 'experience', label: 'Stories Only' }
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => { setFilterType(opt.key); }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${filterType === opt.key ? 'text-primary font-medium bg-primary/5' : 'text-foreground hover:bg-secondary/50'}`}
                    data-testid={`activity-filter-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
                <div className="border-t border-border my-1" />
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Sort</div>
                {[
                  { key: 'newest', label: 'Newest First' },
                  { key: 'oldest', label: 'Oldest First' }
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => { setSortOrder(opt.key); }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${sortOrder === opt.key ? 'text-primary font-medium bg-primary/5' : 'text-foreground hover:bg-secondary/50'}`}
                    data-testid={`activity-sort-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {filteredActivity.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground italic" data-testid="activity-empty-state">
              {filterType !== 'all' ? `No ${filterType === 'journal' ? 'entries' : 'stories'} found` : 'No activity yet'}
            </p>
          </div>
        ) : (
          <div className="relative border-l border-border ml-4 md:ml-0 pl-8 md:pl-12 space-y-12" data-testid="activity-timeline">
            {filteredActivity.map((item) => (
              <motion.div key={item.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="relative" data-testid={`activity-item-${item.id}`}>
                <div className="absolute -left-[41px] md:-left-[53px] w-4 h-4 bg-primary rounded-full border-4 border-background" />
                <div className="bg-card border border-border rounded-2xl p-6 hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {item.type === 'journal' ? <BookOpen className="w-5 h-5 text-primary" /> : <Sparkles className="w-5 h-5 text-accent" />}
                      <span className="text-sm text-muted-foreground tracking-wide uppercase">{item.type === 'journal' ? 'Journal Entry' : 'Story'}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{new Date(item.timestamp).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                  {item.type === 'journal' ? (
                    <p className="text-base leading-relaxed font-serif text-foreground whitespace-pre-wrap" data-testid={`activity-journal-content-${item.id}`}><LinkifyText text={item.content} /></p>
                  ) : (
                    <div>
                      <p className="text-base leading-relaxed text-foreground mb-2" data-testid={`activity-story-summary-${item.id}`}><LinkifyText text={item.summary} /></p>
                      <span className="inline-block text-xs bg-primary/10 text-primary px-3 py-1 rounded-full" data-testid={`activity-story-folder-${item.id}`}>{item.topic_folder}</span>
                    </div>
                  )}
                  <button
                    onClick={() => handleViewItem(item)}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                    data-testid={`activity-view-btn-${item.id}`}
                  >
                    <ExternalLink className="w-4 h-4" />
                    {item.type === 'journal' ? 'View Entry' : 'View Story'}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default ActivityPage;
